import React, { useContext, useRef, useState } from "react";
import Card from "../../../Card";
import EmptyState from "../../../EmptyState";
import MainContext from "../../../../Context/MainContext/MainContext";
import DashboardWidget from "../../../../Utility/Crud/DashboardWidget";
import Widget from "../../../../Utility/Crud/Widget";
import MUIGrid from "@mui/material/Grid";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUITextField from "@mui/material/TextField";
import MUIButton from "@mui/material/Button";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import MUITableContainer from "@mui/material/TableContainer";
import MUITable from "@mui/material/Table";
import MUITableHead from "@mui/material/TableHead";
import MUITableBody from "@mui/material/TableBody";
import MUITableRow from "@mui/material/TableRow";
import MUITableCell from "@mui/material/TableCell";
import MUIPaper from "@mui/material/Paper";
import Menu from "../../../Menu";
import ChipWidget from "../../../Chip/ChipWidget";
import MUIAddIcon from "@mui/icons-material/Add";
import MUIDeleteIcon from "@mui/icons-material/Delete";
import MUIArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import MUIArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import MUIExpandLessIcon from "@mui/icons-material/ExpandLess";
import MUIExpandMoreIcon from "@mui/icons-material/ExpandMore";

export default function CardAdminDashboardWidgets(props) {
  const context = useContext(MainContext);

  const [addDashboardWidgetWidget, setAddDashboardWidgetWidget] =
    useState(null);
  const [addDashboardWidgetName, setAddDashboardWidgetName] = useState("");
  const [expandedByDashboardWidgetId, setExpandedByDashboardWidgetId] =
    useState({});
  const nextNegativeIdRef = useRef(-1);

  /**
   * Returns dashboardWidgets sorted by ordinal.
   */
  const getDashboardWidgets = () => {
    return [...props.dashboardWidgets].sort((a, b) => a.ordinal - b.ordinal);
  };

  /**
   * Adds a new dashboardWidget to local draft state and expands it for editing.
   * New draft rows use negative ids until persisted.
   */
  const handleAddDashboardWidget = () => {
    const addDashboardWidgetNameTrimmed = addDashboardWidgetName.trim();
    const existingDashboardWidgets = getDashboardWidgets();
    const newDashboardWidget = {
      id: nextNegativeIdRef.current,
      dashboardId: props.dashboard.id,
      widgetId: addDashboardWidgetWidget.id,
      name: addDashboardWidgetNameTrimmed,
      ordinal: existingDashboardWidgets.length + 1,
      jsonConfiguration: "",
    };
    nextNegativeIdRef.current -= 1;

    props.onChange(existingDashboardWidgets.concat([newDashboardWidget]));
    setAddDashboardWidgetWidget(null);
    setAddDashboardWidgetName("");
    setExpandedByDashboardWidgetId((prevState) => ({
      ...prevState,
      [newDashboardWidget.id]: true,
    }));
  };

  /**
   * Removes a dashboardWidget from draft state and re-normalizes ordinals.
   */
  const handleRemoveDashboardWidget = (id) => {
    const dashboardWidgets = getDashboardWidgets()
      .filter((dashboardWidget) => dashboardWidget.id !== id)
      .map((dashboardWidget, index) => ({
        ...dashboardWidget,
        ordinal: index + 1,
      }));

    props.onChange(dashboardWidgets);
    setExpandedByDashboardWidgetId((prevState) => {
      const { [id]: _deleted, ...rest } = prevState;
      return rest;
    });
  };

  /**
   * Moves a dashboardWidget up/down and re-normalizes ordinals.
   */
  const handleMoveDashboardWidget = (id, direction) => {
    const dashboardWidgets = getDashboardWidgets();
    const index = dashboardWidgets.findIndex(
      (dashboardWidget) => dashboardWidget.id === id,
    );
    const targetIndex = index + direction;

    const reorderedDashboardWidgets = [...dashboardWidgets];
    const [movedDashboardWidget] = reorderedDashboardWidgets.splice(index, 1);
    reorderedDashboardWidgets.splice(targetIndex, 0, movedDashboardWidget);

    props.onChange(
      reorderedDashboardWidgets.map(
        (dashboardWidget, dashboardWidgetIndex) => ({
          ...dashboardWidget,
          ordinal: dashboardWidgetIndex + 1,
        }),
      ),
    );
  };

  /**
   * Applies field changes to a single dashboardWidget in draft state.
   */
  const handleChangeDashboardWidget = (dashboardWidget, changes) => {
    const dashboardWidgets = getDashboardWidgets().map(
      (dashboardWidgetEntry) => {
        if (dashboardWidgetEntry.id === dashboardWidget.id) {
          return {
            ...dashboardWidgetEntry,
            ...changes,
          };
        }
        return dashboardWidgetEntry;
      },
    );
    props.onChange(dashboardWidgets);
  };

  /**
   * Renders add controls plus an accordion-style table for dashboardWidgets.
   */
  const renderContent = () => {
    const dashboardWidgets = getDashboardWidgets();
    const allWidgets = Object.values(context.widgetsIndexed).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    const canAddDashboardWidget =
      !!addDashboardWidgetWidget && addDashboardWidgetName.trim().length > 0;
    const validationIssues = props.validationIssues;

    return (
      <>
        <MUIGrid container spacing={2} sx={{ marginBottom: 2 }}>
          <MUIGrid item xs={12} md={5}>
            <MUIAutocomplete
              size="small"
              disableClearable={true}
              options={allWidgets}
              getOptionLabel={(widget) => widget.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={addDashboardWidgetWidget}
              onChange={(e, value) => {
                if (addDashboardWidgetName.trim() === "") {
                  setAddDashboardWidgetName(Widget.getDisplayName(value));
                }
                setAddDashboardWidgetWidget(value);
              }}
              renderInput={(params) => (
                <MUITextField
                  {...params}
                  label="Widget"
                  variant="standard"
                  fullWidth={true}
                  required={true}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </MUIGrid>
          <MUIGrid item xs={12} md={5}>
            <MUITextField
              size="small"
              label="Name"
              variant="standard"
              fullWidth={true}
              required={true}
              value={addDashboardWidgetName}
              InputLabelProps={{ shrink: true }}
              onBlur={(e) => setAddDashboardWidgetName(e.target.value.trim())}
              onChange={(e) => setAddDashboardWidgetName(e.target.value)}
            />
          </MUIGrid>
          <MUIGrid item xs={12} md={2}>
            <MUIButton
              fullWidth={true}
              variant="outlined"
              startIcon={<MUIAddIcon />}
              disabled={!canAddDashboardWidget}
              onClick={handleAddDashboardWidget}
              sx={{ whiteSpace: "nowrap" }}
            >
              Add Widget
            </MUIButton>
          </MUIGrid>
        </MUIGrid>

        {dashboardWidgets.length === 0 ? (
          <EmptyState
            line1="Not Configured"
            line2="This dashboard has no linked widgets"
          />
        ) : (
          <MUITableContainer component={MUIPaper} elevation={0} sx={{ mt: 1 }}>
            <MUITable size="small">
              <MUITableHead
                sx={{
                  backgroundColor: (theme) => theme.palette.background.default,
                }}
              >
                <MUITableRow>
                  <MUITableCell sx={{ width: 50 }} />
                  <MUITableCell sx={{ width: 220 }}>Widget</MUITableCell>
                  <MUITableCell>Name</MUITableCell>
                  <MUITableCell align="right" sx={{ width: 50 }} />
                </MUITableRow>
              </MUITableHead>
              <MUITableBody>
                {dashboardWidgets.map(
                  (dashboardWidget, dashboardWidgetIndex) => {
                    const expanded =
                      expandedByDashboardWidgetId[dashboardWidget.id] === true;
                    const widgetRecord =
                      context.widgetsIndexed[dashboardWidget.widgetId];
                    const dashboardWidgetValidationIssues =
                      validationIssues[dashboardWidget.id] || {};
                    const canMoveUp = dashboardWidgetIndex > 0;
                    const canMoveDown =
                      dashboardWidgetIndex < dashboardWidgets.length - 1;
                    const menuItems = [
                      <MUIMenuItem
                        key="move-up"
                        disabled={!canMoveUp}
                        onClick={() =>
                          handleMoveDashboardWidget(dashboardWidget.id, -1)
                        }
                      >
                        <MUIListItemIcon>
                          <MUIArrowUpwardIcon fontSize="small" />
                        </MUIListItemIcon>
                        Move Up
                      </MUIMenuItem>,
                      <MUIMenuItem
                        key="move-down"
                        disabled={!canMoveDown}
                        onClick={() =>
                          handleMoveDashboardWidget(dashboardWidget.id, 1)
                        }
                      >
                        <MUIListItemIcon>
                          <MUIArrowDownwardIcon fontSize="small" />
                        </MUIListItemIcon>
                        Move Down
                      </MUIMenuItem>,
                      <MUIMenuItem
                        key="delete"
                        onClick={() =>
                          handleRemoveDashboardWidget(dashboardWidget.id)
                        }
                      >
                        <MUIListItemIcon>
                          <MUIDeleteIcon fontSize="small" />
                        </MUIListItemIcon>
                        Delete
                      </MUIMenuItem>,
                    ];

                    return [
                      <MUITableRow
                        key={`dashboard-widget-${dashboardWidget.id}`}
                        hover={true}
                        sx={{
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setExpandedByDashboardWidgetId((prevState) => ({
                            ...prevState,
                            [dashboardWidget.id]: !expanded,
                          }));
                        }}
                      >
                        <MUITableCell
                          sx={{
                            width: 30,
                            color: (theme) => theme.palette.text.disabled,
                          }}
                        >
                          {expanded ? (
                            <MUIExpandLessIcon fontSize="small" />
                          ) : (
                            <MUIExpandMoreIcon fontSize="small" />
                          )}
                        </MUITableCell>
                        <MUITableCell sx={{ width: 220 }}>
                          <ChipWidget widget={widgetRecord} />
                        </MUITableCell>
                        <MUITableCell>
                          {DashboardWidget.getDisplayName(dashboardWidget)}
                        </MUITableCell>
                        <MUITableCell
                          align="right"
                          sx={{ width: 30 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Menu
                            menuItems={menuItems}
                            tooltip="Widget Actions"
                          />
                        </MUITableCell>
                      </MUITableRow>,
                      expanded && (
                        <MUITableRow
                          key={`dashboard-widget-expanded-${dashboardWidget.id}`}
                        >
                          <MUITableCell colSpan={4}>
                            <MUIGrid container spacing={2}>
                              <MUIGrid item xs={12} md={6}>
                                <MUIAutocomplete
                                  size="small"
                                  disableClearable={true}
                                  options={allWidgets}
                                  getOptionLabel={(widget) => widget.name}
                                  isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                  }
                                  value={
                                    context.widgetsIndexed[
                                      dashboardWidget.widgetId
                                    ]
                                  }
                                  onChange={(e, value) => {
                                    handleChangeDashboardWidget(
                                      dashboardWidget,
                                      {
                                        widgetId: value.id,
                                        ...((
                                          dashboardWidget.name ?? ""
                                        ).trim() === ""
                                          ? {
                                              name: Widget.getDisplayName(
                                                value,
                                              ),
                                            }
                                          : {}),
                                      },
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <MUITextField
                                      {...params}
                                      label="Widget"
                                      variant="standard"
                                      required={true}
                                      fullWidth={true}
                                      InputLabelProps={{ shrink: true }}
                                    />
                                  )}
                                />
                              </MUIGrid>
                              <MUIGrid item xs={12} md={6}>
                                <MUITextField
                                  label="Name"
                                  variant="standard"
                                  required={true}
                                  fullWidth={true}
                                  value={dashboardWidget.name ?? ""}
                                  InputLabelProps={{ shrink: true }}
                                  error={
                                    dashboardWidgetValidationIssues.name
                                      ? true
                                      : false
                                  }
                                  helperText={
                                    dashboardWidgetValidationIssues.name || ""
                                  }
                                  onBlur={(e) =>
                                    handleChangeDashboardWidget(
                                      dashboardWidget,
                                      { name: e.target.value.trim() },
                                    )
                                  }
                                  onChange={(e) =>
                                    handleChangeDashboardWidget(
                                      dashboardWidget,
                                      { name: e.target.value },
                                    )
                                  }
                                />
                              </MUIGrid>
                              <MUIGrid item xs={12}>
                                <MUITextField
                                  label="Configuration"
                                  variant="standard"
                                  size="small"
                                  fullWidth={true}
                                  value={
                                    dashboardWidget.jsonConfiguration ?? ""
                                  }
                                  placeholder="Please configure this widget with valid JSON."
                                  InputLabelProps={{ shrink: true }}
                                  InputProps={{
                                    sx: {
                                      fontFamily: "monospace",
                                    },
                                  }}
                                  error={
                                    dashboardWidgetValidationIssues.jsonConfiguration
                                      ? true
                                      : false
                                  }
                                  helperText={
                                    dashboardWidgetValidationIssues.jsonConfiguration ||
                                    ""
                                  }
                                  onChange={(e) =>
                                    handleChangeDashboardWidget(
                                      dashboardWidget,
                                      { jsonConfiguration: e.target.value },
                                    )
                                  }
                                />
                              </MUIGrid>
                            </MUIGrid>
                          </MUITableCell>
                        </MUITableRow>
                      ),
                    ];
                  },
                )}
              </MUITableBody>
            </MUITable>
          </MUITableContainer>
        )}
      </>
    );
  };

  return <Card renderContent={renderContent} getTitle={() => "Widgets"} />;
}
